import React, { ReactElement } from 'react';
import { ApiVersion, StandardTask, TeamPermission, useProjectTasksService } from 'nekst-api';
import {
  AfterSubmitFunc,
  CheckboxInput,
  FormRows,
  FormTemplate,
  IfFormData,
  RelativeRow,
  TextArea,
  TextInput,
} from 'ui-builder';
import {
  DueDateInput, PortalRoadmapCheckbox,
  ProjectLinkedDetailFieldDropdown,
  ProjectParentTaskRelationInput,
  RoadmapDescription
} from 'features/tasks-forms';
import Row3x3x6x from '../../../../../shared/web/uitheme/form/Row3x3x6x';
import Separator from '../../../../../shared/web/uitheme/form/Separator';
import TaskAssignmentsInput from './TaskAssignmentsInput';
import TaskCommentsListInput from '../comments/TaskCommentsListInput';
import ProjectDropdown from '../ProjectDropdown';
import { IfWeekendDropdown, ProjectIdContext } from 'features/nekst-widgets';
import { useAuthorizationChecker } from 'authorization-scope';
import useFeatureToggleService, { ToggledFeature } from 'feature-toggle';
import { EmptyCell } from '../../../../../shared/web/uitheme/form/RelativeRow';

export interface BaseStandardTaskFormProps {
  submitFormFunc: (data: StandardTask) => Promise<StandardTask>;
  afterSubmitFunc?: AfterSubmitFunc,
  title: string | ReactElement;
  buttons: ReactElement;
  initialData?: StandardTask;
  isDisabled?: boolean | ((data: StandardTask) => boolean)
  showProjectDropdown?: boolean
  children?: ReactElement,
}

function CheckboxesRow(props: { initialData?: StandardTask }) {
  const { isGranted } = useAuthorizationChecker();

  if (isGranted(TeamPermission.FEATURE_BUSINESS_DAYS) && props.initialData?.parentTaskRelation) {
    return (
      <RelativeRow weights={[2.5, 2.5, 3, 4]} useFlex toBottom>
        <CheckboxInput source="isKeyDate" label="Key Date" />
        <CheckboxInput source="isPrivate" label="Private" />
        <CheckboxInput source="useBusinessDaysCalculation" label="Business Days" />
        <PortalRoadmapCheckbox />
      </RelativeRow>
    );
  } else {
    return (
      <Row3x3x6x>
        <CheckboxInput source="isKeyDate" label="Key Date" />
        <CheckboxInput source="isPrivate" label="Private" />
        <PortalRoadmapCheckbox />
      </Row3x3x6x>
    );
  }
}

function DueDateRowForDependentTask() {
  const featureToggleService = useFeatureToggleService();

  if (featureToggleService.isFeatureEnabled(ToggledFeature.BUSINESS_DAYS)) {
    return (
      <FormRows>
        <RelativeRow weights={[8, 4]}>
          <ProjectParentTaskRelationInput source="parentTaskRelation" label="Due" />
          <RelativeRow weights={[1, 11]}>
            <EmptyCell />
            <IfWeekendDropdown />
          </RelativeRow>
        </RelativeRow>
        <RelativeRow weights={[4, 8]}>
          <ProjectLinkedDetailFieldDropdown label="Link To Detail Box" />
        </RelativeRow>
      </FormRows>
    );
  } else {
    return (
      <FormRows>
        <RelativeRow weights={[8, 4]} useFlex toBottom>
          <ProjectParentTaskRelationInput source="parentTaskRelation" label="Due" />
          <ProjectLinkedDetailFieldDropdown label="Link To Detail Box" />
        </RelativeRow>
      </FormRows>
    );
  }

}

export default function BaseStandardTaskForm(props: BaseStandardTaskFormProps) {
  const projectTasksService = useProjectTasksService();

  const isDisabled = props.isDisabled ?? false;

  const showProjectDropdown = props.showProjectDropdown || false;


  const featureToggleService = useFeatureToggleService();

  return (
    <ProjectIdContext projectId={props.initialData?.projectId || 0}>
      <FormTemplate<StandardTask & { copyRoadmapDescription?: boolean }>
        title={props.title}
        submitFormFunc={props.submitFormFunc}
        afterSubmitFunc={props.afterSubmitFunc}
        buttons={props.buttons}
        initialData={{
          copyRoadmapDescription: !props.initialData?.roadmapDescription,
          ...props.initialData || {},
          _version: featureToggleService.isFeatureEnabled(ToggledFeature.BUSINESS_DAYS) ? ApiVersion.IF_WEEKEND_SHIFT : undefined
        }}
        isDisabled={isDisabled}
        validationSchema={projectTasksService.getStandardTaskValidationSchema()}
      >
        {showProjectDropdown && (<ProjectDropdown className="form-row" source="projectId" />)}
        <TextInput source="name" label="Task Name" />
        <CheckboxesRow initialData={props.initialData} />
        <RelativeRow
          weights={[7, 5]}
          isVisible={(data) => !data.parentTaskRelation}
        >
          <DueDateInput />
          <ProjectLinkedDetailFieldDropdown label="Link To Detail Box" />
        </RelativeRow>
        <IfFormData conditionFunc={(data) => !!data.parentTaskRelation}>
          <DueDateRowForDependentTask />
        </IfFormData>
        <Separator />
        <TextArea source="description" label="Task Description" />
        <RoadmapDescription />
        <Separator />
        <TaskAssignmentsInput source="assignments" label="Transaction Parties" />
        <Separator isVisible={(data) => data.id} />
        <TaskCommentsListInput isVisible={(data) => data.id} />
        {props.children || null}
      </FormTemplate>
    </ProjectIdContext>
  );
}
